import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import CustomSelect from '../common/CustomSelect';

// Type definitions
interface ContactProfile {
  contact_id: number;
  profile_name: string;
  name_first: string;
  name_last: string;
  email: string;
  phone: string;
  address_line1: string;
  city: string;
  state_province: string;
  postal_code: string;
  country: string;
  is_default: boolean;
}

interface ValidationResponse {
  available: boolean;
  domain: string;
  price?: number;
  currency?: string;
  period?: number;
  message?: string;
  authorized?: boolean;
  authorizationMessage?: string;
}

// Backend response schema for availability check
interface AvailabilityCheckResponse {
  available: Array<{
    domain: string;
    available: boolean;
    price?: number;
    currency?: string;
  }>;
  unavailable: Array<{
    domain: string;
    available: boolean;
    error?: string;
  }>;
  total_checked: number;
}

interface PurchaseResult {
  orderId: string;
  domain: string;
  totalCost: number;
}

interface DomainPurchaseWizardProps {
  projectId: number;
  onComplete?: (result: PurchaseResult) => void;
  onCancel?: () => void;
}

const DomainPurchaseWizard: React.FC<DomainPurchaseWizardProps> = ({
  projectId,
  onComplete,
  onCancel
}) => {
  // Authentication check
  const [authToken, setAuthToken] = useState<string | null>(null);

  // Step management
  const [currentStep, setCurrentStep] = useState(1);

  // Step 1: Domain input
  const [domain, setDomain] = useState('');
  const [domainError, setDomainError] = useState('');

  // Step 1: Availability check (combined with domain input)
  const [validationResult, setValidationResult] = useState<ValidationResponse | null>(null);
  const [validating, setValidating] = useState(false);
  const [validationError, setValidationError] = useState('');

  // Step 2: Contact profiles
  const [contactProfiles, setContactProfiles] = useState<ContactProfile[]>([]);
  const [selectedContactId, setSelectedContactId] = useState<number | null>(null);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [contactsError, setContactsError] = useState('');

  // New contact profile form
  const [showNewProfileForm, setShowNewProfileForm] = useState(false);
  const [creatingProfile, setCreatingProfile] = useState(false);
  const [newProfileError, setNewProfileError] = useState('');
  const [newProfile, setNewProfile] = useState({
    profile_name: '',
    name_first: '',
    name_last: '',
    email: '',
    phone: '',
    organization: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state_province: '',
    postal_code: '',
    country: 'US'
  });

  // Step 3: Purchase options
  const [years, setYears] = useState(1);
  const [autoRenew, setAutoRenew] = useState(false);

  // Step 4: Purchase submission
  const [purchasing, setPurchasing] = useState(false);
  const [purchaseError, setPurchaseError] = useState('');

  // Initialize auth token
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    setAuthToken(token);
  }, []);

  // Domain validation function
  const validateDomainFormat = (domainInput: string): boolean => {
    // Basic domain validation
    if (!domainInput) {
      setDomainError('Domain name is required');
      return false;
    }

    // Check for TLD (must have at least one dot)
    if (!domainInput.includes('.')) {
      setDomainError('Invalid domain format');
      return false;
    }

    // Check length (max 253 characters for full domain)
    if (domainInput.length > 63) {
      setDomainError('Domain name too long');
      return false;
    }

    // Check for invalid characters
    const validDomainPattern = /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;
    if (!validDomainPattern.test(domainInput)) {
      setDomainError('Invalid domain format');
      return false;
    }

    setDomainError('');
    return true;
  };

  // Handle Check Availability button click
  const handleCheckAvailability = async () => {
    if (!validateDomainFormat(domain)) {
      return;
    }
    await validateDomain();
  };

  // Handle Step 1 Next (proceed to Contact Selection)
  const handleStep1Next = () => {
    if (validationResult?.available) {
      setCurrentStep(2);
      loadContactProfiles();
    }
  };

  // Validate domain availability
  const validateDomain = async () => {
    setValidating(true);
    setValidationError('');

    try {
      // Use the availability endpoint which doesn't require contact info
      const response = await fetch(
        `/api/v1/godaddy/projects/${projectId}/domains/availability`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            domains: [domain]
          })
        }
      );

      if (!response.ok) {
        let errorMessage = 'Failed to check domain availability';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch (e) {
          // Use default message if JSON parsing fails
        }
        setValidationError(errorMessage);
        setValidationResult(null);
        return;
      }

      const backendData: AvailabilityCheckResponse = await response.json();

      // Check if domain is in available or unavailable list
      const availableDomain = backendData.available?.find(d => d.domain === domain);
      const unavailableDomain = backendData.unavailable?.find(d => d.domain === domain);

      if (availableDomain) {
        // Domain is available
        const data: ValidationResponse = {
          available: true,
          domain: domain,
          price: availableDomain.price,
          currency: availableDomain.currency || 'USD',
          period: 1
        };
        setValidationResult(data);
      } else if (unavailableDomain) {
        // Domain is not available
        const data: ValidationResponse = {
          available: false,
          domain: domain,
          message: unavailableDomain.error || 'Domain is not available for registration'
        };
        setValidationResult(data);
        setValidationError(data.message || 'Domain is not available');
      } else {
        // Unexpected response
        setValidationError('Unable to determine domain availability');
        setValidationResult(null);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to check domain availability';
      setValidationError(errorMessage);
    } finally {
      setValidating(false);
    }
  };

  // Load contact profiles
  const loadContactProfiles = async () => {
    setLoadingContacts(true);
    setContactsError('');

    try {
      const response = await fetch(`/api/v1/projects/${projectId}/contacts`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        setContactsError(errorData.detail || 'Failed to load contact profiles');
        setContactProfiles([]);
        return;
      }

      const data: ContactProfile[] = await response.json();
      setContactProfiles(data);

      // Auto-select default profile
      const defaultProfile = data.find(p => p.is_default);
      if (defaultProfile) {
        setSelectedContactId(defaultProfile.contact_id);
      }
    } catch (error) {
      setContactsError('Failed to load contact profiles');
      setContactProfiles([]);
    } finally {
      setLoadingContacts(false);
    }
  };

  // Create new contact profile
  const handleCreateProfile = async () => {
    setCreatingProfile(true);
    setNewProfileError('');

    // Basic validation
    const requiredFields = ['profile_name', 'name_first', 'name_last', 'email', 'phone', 'address_line1', 'city', 'state_province', 'postal_code', 'country'];
    const missingFields = requiredFields.filter(field => !newProfile[field as keyof typeof newProfile]);
    if (missingFields.length > 0) {
      setNewProfileError(`Please fill in all required fields: ${missingFields.join(', ')}`);
      setCreatingProfile(false);
      return;
    }

    try {
      const response = await fetch(`/api/v1/projects/${projectId}/contacts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newProfile)
      });

      if (!response.ok) {
        let errorMessage = 'Failed to create contact profile';
        try {
          const errorData = await response.json();
          // Handle FastAPI validation errors (detail is an array)
          if (Array.isArray(errorData.detail)) {
            errorMessage = errorData.detail.map((e: { msg: string; loc?: (string | number)[] }) => {
              // Get field name from loc array (last element is usually the field name)
              const field = e.loc && e.loc.length > 0 ? e.loc[e.loc.length - 1] : '';
              return field ? `${field}: ${e.msg}` : e.msg;
            }).join(', ');
          } else if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          }
        } catch (e) {
          // Use default message
        }
        setNewProfileError(errorMessage);
        return;
      }

      const createdProfile: ContactProfile = await response.json();

      // Add to list and select it
      setContactProfiles(prev => [...prev, createdProfile]);
      setSelectedContactId(createdProfile.contact_id);

      // Reset form and hide it
      setShowNewProfileForm(false);
      setNewProfile({
        profile_name: '',
        name_first: '',
        name_last: '',
        email: '',
        phone: '',
        organization: '',
        address_line1: '',
        address_line2: '',
        city: '',
        state_province: '',
        postal_code: '',
        country: 'US'
      });

      toast.success('Contact profile created');
    } catch (error) {
      setNewProfileError('Failed to create contact profile');
    } finally {
      setCreatingProfile(false);
    }
  };

  // Handle Step 2 Next (Contact Selection -> Options)
  const handleStep2Next = () => {
    if (selectedContactId) {
      setCurrentStep(3);
    }
  };

  // Handle Step 3 Next (Options -> Review)
  const handleStep3Next = () => {
    setCurrentStep(4);
  };

  // Handle purchase submission
  const handlePurchaseSubmit = async () => {
    setPurchasing(true);
    setPurchaseError('');

    try {
      const response = await fetch(`/api/v1/godaddy/purchase`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          domain,
          project_id: projectId,
          contact_profile_id: selectedContactId,
          years,
          auto_renew: autoRenew
        })
      });

      if (!response.ok) {
        let errorMessage = 'Failed to complete purchase';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch (e) {
          // Use default message if JSON parsing fails
        }
        setPurchaseError(errorMessage);
        toast.error(errorMessage);
        return;
      }

      const data = await response.json();
      const successMessage = 'Purchase successful';
      toast.success(successMessage);

      if (onComplete) {
        onComplete({
          orderId: data.order_id,
          domain: data.domain,
          totalCost: data.total_cost
        });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to complete purchase';
      setPurchaseError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setPurchasing(false);
    }
  };

  // Navigation handlers
  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  const handleEdit = (step: number) => {
    setCurrentStep(step);
  };

  // Check authentication
  if (!authToken) {
    return (
      <div className="p-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
        <p className="text-red-600">Authentication required</p>
      </div>
    );
  }

  // Format price for display (backend returns prices in cents)
  const formatPrice = (price: number, currency: string = 'USD'): string => {
    const dollars = (price / 100).toFixed(2);
    return `$${dollars}`;
  };

  // Get selected contact profile
  const selectedContact = contactProfiles.find(p => p.contact_id === selectedContactId);

  // Render step indicator
  const stepLabels = ['Search', 'Contact', 'Options', 'Review'];
  const renderStepIndicator = () => (
    <div className="flex justify-between mb-8">
      {[1, 2, 3, 4].map((step) => (
        <div
          key={step}
          className={`flex flex-col items-center ${step === currentStep ? 'active' : ''}`}
        >
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center mb-2 ${
              step === currentStep
                ? 'bg-primary-600 text-white'
                : step < currentStep
                ? 'bg-green-600 text-white'
                : 'bg-gray-300 text-gray-600 dark:text-gray-400'
            }`}
          >
            {step}
          </div>
          <span className={`text-xs ${step === currentStep ? 'active font-semibold' : 'text-gray-500 dark:text-gray-400'}`}>
            {stepLabels[step - 1]}
          </span>
          {step < 4 && (
            <div
              className={`absolute top-4 left-1/2 w-16 h-1 ${
                step < currentStep ? 'bg-green-600' : 'bg-gray-300'
              }`}
              style={{ transform: 'translateX(50%)' }}
            />
          )}
        </div>
      ))}
    </div>
  );

  // Render Step 1: Domain Search (combined input + availability check)
  const renderStep1 = () => (
    <div>
      <h2 className="text-2xl font-bold mb-6">Search Domain</h2>
      <div className="mb-4">
        <label htmlFor="domain-input" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Domain Name
        </label>
        <div className="flex gap-2">
          <input
            id="domain-input"
            type="text"
            value={domain}
            onChange={(e) => {
              setDomain(e.target.value);
              setDomainError('');
              // Clear previous availability result when domain changes
              setValidationResult(null);
              setValidationError('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && domain && !validating) {
                handleCheckAvailability();
              }
            }}
            placeholder="example.com"
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
          <button
            onClick={handleCheckAvailability}
            disabled={!domain || validating}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {validating ? 'Checking...' : 'Check Availability'}
          </button>
        </div>
        {domainError && (
          <p className="mt-2 text-sm text-red-600">{domainError}</p>
        )}
      </div>

      {/* Availability Result */}
      {validating ? (
        <div className="text-center py-6">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Checking availability...</p>
        </div>
      ) : validationError && !validationResult?.available ? (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
          <p className="text-red-600 font-semibold mb-1">Not Available</p>
          <p className="text-red-600 text-sm">{validationError}</p>
          <p className="text-gray-600 dark:text-gray-400 text-sm mt-2">Try a different domain name above.</p>
        </div>
      ) : validationResult?.available ? (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
          <p className="text-green-600 font-semibold mb-1">Available!</p>
          <p className="text-gray-700 dark:text-gray-300">
            <span className="font-medium">{validationResult.domain}</span> is available for registration.
          </p>
          {validationResult.price && (
            <p className="text-gray-700 dark:text-gray-300 mt-1">
              Price: {formatPrice(validationResult.price, validationResult.currency)}/year
            </p>
          )}
        </div>
      ) : null}

      <div className="flex justify-between mt-6">
        <button
          onClick={handleCancel}
          className="px-6 py-2 bg-gray-300 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-400"
        >
          Cancel
        </button>
        <button
          onClick={handleStep1Next}
          disabled={!validationResult?.available}
          className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>
    </div>
  );

  // Render Step 2: Contact Selection
  const renderStep2 = () => (
    <div>
      <h2 className="text-2xl font-bold mb-6">Select Contact Profile</h2>

      {loadingContacts ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading contact profiles...</p>
        </div>
      ) : contactsError ? (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
          <p className="text-red-600">{contactsError}</p>
        </div>
      ) : showNewProfileForm ? (
        // New Profile Form
        <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4 mb-4">
          <h3 className="text-lg font-semibold mb-4">Create New Contact Profile</h3>

          {newProfileError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-3 mb-4">
              <p className="text-red-600 text-sm">{newProfileError}</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Profile Name *
              </label>
              <input
                type="text"
                value={newProfile.profile_name}
                onChange={(e) => setNewProfile(prev => ({ ...prev, profile_name: e.target.value }))}
                placeholder="e.g., Business Contact"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div className="md:col-span-2 grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  First Name *
                </label>
                <input
                  type="text"
                  value={newProfile.name_first}
                  onChange={(e) => setNewProfile(prev => ({ ...prev, name_first: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Last Name *
                </label>
                <input
                  type="text"
                  value={newProfile.name_last}
                  onChange={(e) => setNewProfile(prev => ({ ...prev, name_last: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email *
              </label>
              <input
                type="email"
                value={newProfile.email}
                onChange={(e) => setNewProfile(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Phone *
              </label>
              <input
                type="tel"
                value={newProfile.phone}
                onChange={(e) => setNewProfile(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+1.5551234567"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Organization
              </label>
              <input
                type="text"
                value={newProfile.organization}
                onChange={(e) => setNewProfile(prev => ({ ...prev, organization: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Address Line 1 *
              </label>
              <input
                type="text"
                value={newProfile.address_line1}
                onChange={(e) => setNewProfile(prev => ({ ...prev, address_line1: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Address Line 2
              </label>
              <input
                type="text"
                value={newProfile.address_line2}
                onChange={(e) => setNewProfile(prev => ({ ...prev, address_line2: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                City *
              </label>
              <input
                type="text"
                value={newProfile.city}
                onChange={(e) => setNewProfile(prev => ({ ...prev, city: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                State/Province *
              </label>
              <input
                type="text"
                value={newProfile.state_province}
                onChange={(e) => setNewProfile(prev => ({ ...prev, state_province: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Postal Code *
              </label>
              <input
                type="text"
                value={newProfile.postal_code}
                onChange={(e) => setNewProfile(prev => ({ ...prev, postal_code: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Country *
              </label>
              <CustomSelect
                value={newProfile.country}
                onChange={(value) => setNewProfile(prev => ({ ...prev, country: value }))}
                options={[
                  { value: 'US', label: 'United States' },
                  { value: 'CA', label: 'Canada' },
                  { value: 'GB', label: 'United Kingdom' },
                  { value: 'AU', label: 'Australia' },
                  { value: 'DE', label: 'Germany' },
                  { value: 'FR', label: 'France' },
                ]}
                aria-label="Country"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={() => {
                setShowNewProfileForm(false);
                setNewProfileError('');
              }}
              disabled={creatingProfile}
              className="px-4 py-2 bg-gray-300 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-400 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateProfile}
              disabled={creatingProfile}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-400"
            >
              {creatingProfile ? 'Creating...' : 'Create Profile'}
            </button>
          </div>
        </div>
      ) : contactProfiles.length === 0 ? (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-4">
          <p className="text-yellow-700 dark:text-yellow-300 mb-2">No contact profiles found</p>
          <button
            onClick={() => setShowNewProfileForm(true)}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Create New Profile
          </button>
        </div>
      ) : (
        <div className="space-y-3 mb-6">
          {contactProfiles.map((profile) => (
            <label
              key={profile.contact_id}
              className={`block p-4 border rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 ${
                selectedContactId === profile.contact_id
                  ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-300 dark:border-gray-600'
              }`}
            >
              <input
                type="radio"
                name="contact-profile"
                value={profile.contact_id}
                checked={selectedContactId === profile.contact_id}
                onChange={() => setSelectedContactId(profile.contact_id)}
                className="mr-3"
                aria-label={profile.profile_name}
              />
              <span className="font-semibold">{profile.profile_name}</span>
              <span className="text-sm text-gray-600 dark:text-gray-400 ml-2">
                {profile.name_first} {profile.name_last} - {profile.email}
              </span>
            </label>
          ))}
        </div>
      )}

      {contactProfiles.length > 0 && !showNewProfileForm && (
        <button
          onClick={() => setShowNewProfileForm(true)}
          className="mb-4 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300"
        >
          Create New Profile
        </button>
      )}

      {!showNewProfileForm && (
        <div className="flex justify-between">
          <button
            onClick={handleBack}
            className="px-6 py-2 bg-gray-300 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-400"
          >
            Back
          </button>
          <div className="flex gap-3">
            <button
              onClick={handleCancel}
              className="px-6 py-2 bg-gray-300 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-400"
            >
              Cancel
            </button>
            <button
              onClick={handleStep2Next}
              disabled={!selectedContactId}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );

  // Render Step 3: Options
  const renderStep3 = () => {
    const yearlyPrice = validationResult?.price || 0;
    const totalPrice = yearlyPrice * years;

    return (
      <div>
        <h2 className="text-2xl font-bold mb-6">Configure Options</h2>

        <div className="space-y-6 mb-6">
          {/* Registration Period */}
          <div>
            <label htmlFor="years-select" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Registration Period
            </label>
            <CustomSelect
              value={String(years)}
              onChange={(value) => setYears(parseInt(value))}
              options={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((year) => ({
                value: String(year),
                label: `${year} ${year === 1 ? 'year' : 'years'} - ${formatPrice(yearlyPrice * year)}`,
              }))}
              aria-label="Registration Period"
            />
            {yearlyPrice > 0 && (
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                Total: <span className="font-semibold">{formatPrice(totalPrice)}</span> for {years} {years === 1 ? 'year' : 'years'}
              </p>
            )}
          </div>

          {/* Auto-Renew */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <label className="flex items-start cursor-pointer">
              <input
                type="checkbox"
                checked={autoRenew}
                onChange={(e) => setAutoRenew(e.target.checked)}
                className="mt-1 mr-3"
                aria-label="Auto-Renew"
              />
              <div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Auto-Renew</span>
                {yearlyPrice > 0 && (
                  <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                    ({formatPrice(yearlyPrice)}/year)
                  </span>
                )}
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Automatically renew your domain before it expires to prevent losing it.
                </p>
              </div>
            </label>
          </div>

          {/* Domain Privacy Info */}
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-green-800 dark:text-green-300">Domain Privacy Included</span>
                  <span className="text-xs bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 px-2 py-0.5 rounded-full font-medium">
                    Free
                  </span>
                </div>
                <p className="text-sm text-green-700 dark:text-green-400 mt-1">
                  Your contact info will be replaced with Domains By Proxy in public WHOIS lookups, keeping your personal details private.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-between">
          <button
            onClick={handleBack}
            className="px-6 py-2 bg-gray-300 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-400"
          >
            Back
          </button>
          <button
            onClick={handleStep3Next}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  // Render Step 4: Review
  const renderStep4 = () => (
    <div>
      <h2 className="text-2xl font-bold mb-6">Review Your Purchase</h2>

      <div className="space-y-4 mb-6">
        <div className="flex justify-between items-start border-b pb-3">
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Domain</p>
            <p className="font-semibold">{domain}</p>
          </div>
          <button
            onClick={() => handleEdit(1)}
            className="px-3 py-1 text-sm bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300"
          >
            Edit
          </button>
        </div>

        {validationResult?.price && (
          <div className="flex justify-between items-start border-b pb-3">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Price</p>
              <p className="font-semibold">
                {formatPrice(validationResult.price, validationResult.currency)}
              </p>
            </div>
          </div>
        )}

        {selectedContact && (
          <div className="flex justify-between items-start border-b pb-3">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Contact Profile</p>
              <p className="font-semibold">{selectedContact.profile_name}</p>
            </div>
            <button
              onClick={() => handleEdit(2)}
              className="px-3 py-1 text-sm bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300"
            >
              Edit
            </button>
          </div>
        )}

        <div className="flex justify-between items-start border-b pb-3">
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Registration Period</p>
            <p className="font-semibold">{years} {years === 1 ? 'year' : 'years'}</p>
          </div>
          <button
            onClick={() => handleEdit(3)}
            className="px-3 py-1 text-sm bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300"
          >
            Edit
          </button>
        </div>

        <div className="flex justify-between items-start border-b pb-3">
          <div>
            <p className="font-semibold">Auto-Renew: {autoRenew ? 'Yes' : 'No'}</p>
          </div>
        </div>

        <div className="flex justify-between items-start border-b pb-3">
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Domain Privacy</p>
            <p className="font-semibold">
              Included <span className="text-xs text-green-600">(Free)</span>
            </p>
          </div>
        </div>
      </div>

      {purchaseError && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
          <p className="text-red-600">{purchaseError}</p>
        </div>
      )}

      {purchasing ? (
        <div className="text-center py-4">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Processing purchase...</p>
        </div>
      ) : (
        <div className="flex justify-between">
          <button
            onClick={handleBack}
            className="px-6 py-2 bg-gray-300 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-400"
          >
            Back
          </button>
          <div className="flex gap-3">
            <button
              onClick={handleCancel}
              className="px-6 py-2 bg-gray-300 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-400"
            >
              Cancel
            </button>
            <button
              onClick={handlePurchaseSubmit}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              Submit
            </button>
          </div>
        </div>
      )}
    </div>
  );

  // Render current step
  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      case 4:
        return renderStep4();
      default:
        return null;
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6 bg-white dark:bg-cap-dark rounded-lg shadow-lg">
      <div className="mb-6">
        <p className="text-sm text-gray-600 dark:text-gray-400">Step {currentStep} of 4</p>
      </div>
      {renderStepIndicator()}
      {renderCurrentStep()}
    </div>
  );
};

export default DomainPurchaseWizard;
